/**
 * The checklist inside one task: the four calls that write it, and the two
 * shapes it is read back in.
 *
 * A sibling of `projects.ts` rather than more lines inside it, for the reason
 * `projectPortfolio.ts` gives at its own top: that file is already at the size
 * where the next feature has to go somewhere else (AGENTS.md §4), and a task's
 * checklist is a describable responsibility of its own - one child collection,
 * four endpoints. `projects.ts` re-exports every name below, so an existing
 * `from "./projects.js"` keeps resolving and a caller may use either path. It is
 * deliberately NOT a `projects/` folder with an `index.ts`: this repo has been
 * bitten before by a file-to-folder move emptying the SPA silently.
 *
 * The endpoints sit under two different prefixes, which is the API's shape and
 * not a slip here. Adding and reordering are things done TO A TASK
 * (`/tasks/:id/checklist`), because both need to know whose list is being
 * changed; editing and deleting name the item itself (`/checklist/:itemId`),
 * because an item id is unique on its own and carrying the task in the path
 * would invite a caller to send a pair that does not match.
 *
 * ── TICKING THE LAST BOX DOES NOT COMPLETE THE TASK ──────────────────────────
 *
 * Worth repeating on this side of the wire, because a screen is where somebody
 * will try to add it. No route below touches `Task.status`, `Task.completedAt`
 * or `Task.progress`, and a caller must not chain one of these to `updateTask`
 * either. A checklist is one person's plan for how they mean to get the work
 * done - written before the work starts, routinely half of it, routinely stale.
 * A task is done when a person says so, which is the status control in the page
 * head. `checklist.routes.ts` states the whole argument, including why the
 * reverse (unticking a box reopening a finished task) is worse still.
 */
import { api } from "./api.js";

/** One box on a task's checklist, exactly as the API hands it back. */
export interface ChecklistItem {
  id: string;
  taskId: string;
  text: string;
  done: boolean;
  /**
   * The stored order. Read-only in practice: the server sorts on it and breaks
   * ties on `createdAt`, so a screen draws the rows in the order they ARRIVED
   * rather than sorting them again. Two rows may legitimately share a number -
   * an import writes a whole list at 0 - and a local sort on this field alone
   * would shuffle such a pair between two renders with nobody touching anything.
   */
  sortOrder: number;
  /**
   * A platform `User.id`, or nobody. Nullable rather than defaulted to the
   * task's own assignee: most items belong to nobody in particular (the person
   * working the task is working their own list), and a name on every line would
   * make the ones somebody DID hand over unreadable.
   */
  assignee: string | null;
  createdAt: string;
}

/**
 * Finished boxes out of all boxes, for one task.
 *
 * `{ done: 0, total: 0 }` is the answer for a task with NO checklist, and it is
 * a statement rather than a gap: the list endpoint seeds every row it returns
 * (`foldChecklistProgress` on the API side). So a caller may draw "0/0" for an
 * empty list and draws nothing only while the request is still out - the two
 * are different sentences and only one of them is worth waiting for.
 */
export interface ChecklistProgress {
  done: number;
  total: number;
}

/** Add a box to the end of a task's list. The server picks the `sortOrder`
 *  (one past the highest stored, never the item count - see `lib/checklist.ts`),
 *  so a caller must not send one. */
export const addChecklistItem = (taskId: string, body: { text: string; assignee?: string | null }) =>
  api.post<ChecklistItem>(`/admin/crm/tasks/${taskId}/checklist`, body);

/** Rename one box, tick it, hand it to somebody, or nudge its position. Every
 *  field is optional and an absent one is left alone. */
export const updateChecklistItem = (
  itemId: string,
  body: { text?: string; done?: boolean; assignee?: string | null; sortOrder?: number },
) => api.patch<ChecklistItem>(`/admin/crm/checklist/${itemId}`, body);

export const deleteChecklistItem = (itemId: string) =>
  api.del<{ ok: boolean }>(`/admin/crm/checklist/${itemId}`);

/**
 * The whole list in the order it should now be in, and the stored rows back.
 *
 * The ids go in one body and are renumbered in one transaction, so the list is
 * never half-written: sending the moved item alone would leave every other row's
 * number to be recomputed by the caller, and a move that half-lands is an order
 * nobody can put back by hand. The rows in the answer are the truth to draw -
 * they carry the numbers the server actually stored, which is not always what
 * the caller asked for (an item added by somebody else keeps its own place).
 */
export const reorderChecklist = (taskId: string, ids: string[]) =>
  api.post<{ rows: ChecklistItem[] }>(`/admin/crm/tasks/${taskId}/checklist/reorder`, { ids });
